/** Course dashboard heatmap (STORY-056): objectives × students from the analytics matrix, names
 * from the session roster (analytics never sees them), three fills and colours for the bands,
 * every cell a button that opens the answers behind it. */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, download } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { TeacherChrome } from "../teacher/Chrome";
import { getCourse, getEvidence, getMastery, getRoster, isOfflineError, type Cell, type MatrixLo } from "../teacher/api";
import { Button, SaveNotice, useFileSave } from "../ui";

const BANDS = ["secure", "developing", "not_yet", "unassessed"] as const;
const GLYPH: Record<string, string> = { secure: "■", developing: "▦", not_yet: "□", unassessed: "·" };
const EMPTY: Cell = { score: 0, band: "unassessed", n: 0, last_assessed_at: null };
const WEEK_MS = 7 * 24 * 3600 * 1000;

export function Dashboard() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const matrix = useQuery({ queryKey: ["mastery", courseId], queryFn: () => getMastery(courseId), retry: false });
  const roster = useQuery({ queryKey: ["roster", courseId], queryFn: () => getRoster(courseId) });
  const [picked, setPicked] = useState<{ person: string; lo: MatrixLo } | null>(null);
  const file = useFileSave();
  const evidence = useQuery({
    queryKey: ["evidence", courseId, picked?.person, picked?.lo.lo_id],
    queryFn: () => getEvidence(courseId, picked!.person, picked!.lo.lo_id),
    enabled: !!picked,
  });

  const names = useMemo(() => new Map((roster.data ?? []).map((p) => [p.person_id, p.label])), [roster.data]);
  const los = useMemo(() => (matrix.data?.los ?? []).filter((lo) => lo.review_status === "approved" || lo.assessed > 0), [matrix.data]);
  const people = matrix.data?.respondents ?? [];
  const label = (personId: string, i: number) => names.get(personId) || t("dash.anon", { n: i + 1 });
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }) : t("dash.never"));
  const now = Date.now();
  const attention = los.filter((lo) => lo.assessed > 0 && lo.secure_share < 0.5 && lo.first_taught_at && now - new Date(lo.first_taught_at).getTime() >= WEEK_MS);
  const checkIn = people
    .map((p, i) => ({ p, i, notYet: los.filter((lo) => (p.cells[lo.lo_id] ?? EMPTY).band === "not_yet").length }))
    .filter((x) => x.notYet >= 2)
    .sort((a, b) => b.notYet - a.notYet)
    .slice(0, 8);
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const notProjected = matrix.error instanceof ApiError && matrix.error.status === 404;
  // Without a server there are no projections at all; the lessons are queued, not lost.
  const needsServer = isOfflineError(matrix.error);
  /** The teacher's own CSV: names from the roster joined to the matrix, client-side only. */
  const downloadCsv = async () => {
    const q = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const head = [t("dash.student"), ...los.map((lo) => lo.code), ...los.map((lo) => `${lo.code} ${t("dash.bandCol")}`)];
    const rows = people.map((p, i) => {
      const cells = los.map((lo) => p.cells[lo.lo_id] ?? EMPTY);
      return [label(p.respondent_id, i), ...cells.map((c) => String(c.score)), ...cells.map((c) => t(`dash.bands.${c.band}`))];
    });
    const text = "\ufeff" + [head, ...rows].map((r) => r.map(q).join(",")).join("\n");
    await file.save("mastery.csv", new Blob([text], { type: "text/csv;charset=utf-8" }));
  };
  /** The research export: pseudonymous, produced by analytics. */
  const downloadAnon = async () => {
    file.record(await download(`/analytics/v1/courses/${courseId}/export/events.csv`, "events-anonymised.csv", "csv"));
  };

  return (
    <TeacherChrome title={t("dash.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="dashboard" wide>
      {/* The chrome already prints the page title as the h1; a second one repeated the word
          and gave the screen two competing headings. What is left is the lede and the row of
          exports, every control of it on the same 48 px step (the two links included). */}
      <header className="-mt-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <p className="measure text-sm text-[color:var(--ink-2)]">{t("dash.lede")}</p>
        {matrix.data && people.length > 0 && (
          <div className="print:hidden flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void downloadCsv()}>{t("dash.exportCsv")}</Button>
            <Button variant="secondary" onClick={() => void downloadAnon()}>{t("dash.exportAnon")}</Button>
            <Button variant="secondary" onClick={() => void capabilities.printing.print()}>{t("dash.print")}</Button>
            <Link to="/t/c/$courseId/alignment" params={{ courseId }} className="inline-flex min-h-[var(--control-h)] items-center rounded-[var(--radius-control)] border px-4 text-sm font-medium hover:bg-[color:var(--surface-2)]">{t("dash.alignment")}</Link>
            <Link to="/t/c/$courseId/homework" params={{ courseId }} className="inline-flex min-h-[var(--control-h)] items-center rounded-[var(--radius-control)] border px-4 text-sm font-medium hover:bg-[color:var(--surface-2)]">{t("dash.homework")}</Link>
          </div>
        )}
      </header>
      {/* Where the export went. Silent in a browser, which keeps its own download list. */}
      <div className="print:hidden -mt-2"><SaveNotice result={file.result} /></div>

      {notProjected && <p className={card}>{t("dash.notProjected")}</p>}
      {needsServer && <p className={card}>{t("dash.needsServer")}</p>}
      {matrix.data && (people.length === 0 || los.length === 0) && <p className={card}>{t("dash.empty")}</p>}

      {/* The heatmap takes the space it can use: its cells grow up to a per-course cap (set on
          the table below), so a small course spreads over a wide page instead of stranding the
          grid in a narrow strip, and a large one stays legible and scrolls inside its card.
          Every pixel left over goes to the rail, whose cards then lay themselves out in as
          many columns as they fit (`grid-fit`): on a laptop one under the other, on a wide
          monitor side by side, and never a page half of which is empty. */}
      {matrix.data && people.length > 0 && los.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,auto)_minmax(18rem,1fr)]">
          <section className={`${card} overflow-x-auto`} aria-label={t("dash.title")}>
            <p className="mb-2 text-xs text-[color:var(--ink-2)]">
              {t("dash.people", { count: people.length })} · {t("dash.objectives", { count: los.length })}
            </p>
            {/* Few objectives, wide window: raise the cell cap so the grid spreads instead of
                sitting in a narrow strip. Many objectives: keep them small and let the table
                scroll inside its card. */}
            <table
              className="w-full border-separate border-spacing-1 text-sm"
              style={{ "--cell-max": los.length <= 8 ? "3.75rem" : los.length <= 14 ? "3rem" : "2.75rem" } as CSSProperties}
            >
              <thead>
                <tr>
                  <th scope="col" className="sticky left-0 bg-[color:var(--surface)] text-left font-medium">{t("dash.student")}</th>
                  {los.map((lo) => (
                    <th key={lo.lo_id} scope="col" className="align-bottom font-mono text-xs font-normal" title={lo.text}>
                      <span className="inline-block max-w-11 truncate">{lo.code}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {people.map((p, i) => (
                  <tr key={p.respondent_id}>
                    <th scope="row" className="sticky left-0 max-w-40 truncate bg-[color:var(--surface)] pr-2 text-left font-normal">
                      <Link to="/t/c/$courseId/students/$personId" params={{ courseId, personId: p.respondent_id }} className="underline decoration-dotted">{label(p.respondent_id, i)}</Link>
                    </th>
                    {los.map((lo) => {
                      const c = p.cells[lo.lo_id] ?? EMPTY;
                      return (
                        <td key={lo.lo_id} className="p-0">
                          <button
                            type="button"
                            className={`cell cell-${c.band}`}
                            aria-label={`${label(p.respondent_id, i)} · ${lo.code} · ${t(`dash.bands.${c.band}`)}`}
                            aria-pressed={picked?.person === p.respondent_id && picked.lo.lo_id === lo.lo_id}
                            onClick={() => setPicked({ person: p.respondent_id, lo })}
                          >
                            {GLYPH[c.band]}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" className="sticky left-0 bg-[color:var(--surface)] pr-2 text-left font-medium">{t("dash.classRow")}</th>
                  {los.map((lo) => (
                    <td key={lo.lo_id} className="bignum text-center text-xs">{Math.round(lo.secure_share * 100)}%</td>
                  ))}
                </tr>
              </tfoot>
            </table>
            <p className="mt-3 flex flex-wrap gap-3 text-xs" aria-label={t("dash.legend")}>
              {BANDS.map((b) => (
                <span key={b} className="inline-flex items-center gap-1">
                  <span className={`cell cell-${b} !min-w-5 !max-w-5`} aria-hidden="true">{GLYPH[b]}</span>
                  {t(`dash.bands.${b}`)}
                </span>
              ))}
            </p>
          </section>

          <aside className="grid-fit content-start" style={{ "--fit-min": "20rem", gap: "0.75rem" } as CSSProperties}>
            {picked && (
              <section className={`${card} lg:col-span-full`} aria-live="polite">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <h2 className="font-semibold">{t("dash.evidence")}</h2>
                    <p className="text-xs text-[color:var(--ink-2)]">
                      {label(picked.person, people.findIndex((p) => p.respondent_id === picked.person))} · <span className="font-mono">{picked.lo.code}</span> {picked.lo.text}
                    </p>
                    {(() => {
                      const c = people.find((p) => p.respondent_id === picked.person)?.cells[picked.lo.lo_id] ?? EMPTY;
                      return (
                        <p className="mt-1 text-xs">
                          {t(`dash.bands.${c.band}`)} · {t("dash.score", { pct: Math.round(c.score * 100) })} · {t("dash.n", { count: c.n })} · {t("dash.lastAssessed", { date: fmtDate(c.last_assessed_at) })}
                        </p>
                      );
                    })()}
                  </div>
                  <Button variant="tertiary" onClick={() => setPicked(null)}>{t("dash.close")}</Button>
                </div>
                {evidence.data && evidence.data.rows.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("dash.evidenceNone")}</p>}
                <ul className="flex flex-col gap-2 text-sm">
                  {evidence.data?.rows.map((r) => (
                    <li key={`${r.session_id}-${r.item_id}-${r.attempt}`} className="rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-2">
                      <p className="line-clamp-2">{r.stem}</p>
                      <p className="mt-1 text-xs text-[color:var(--ink-2)]">
                        <span className={r.correct ? "text-[color:var(--secure)]" : "text-[color:var(--not-yet)]"}>{r.correct ? "✓ " : "✗ "}{r.correct ? t("dash.correct") : t("dash.wrong")}</span>
                        {" · "}{fmtDate(r.occurred_at)}{r.attempt > 1 ? ` · ${t("dash.attempt", { n: r.attempt })}` : ""}
                        {r.confidence !== null ? ` · ${t("dash.confidence", { n: r.confidence })}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section className={card}>
              <h2 className="font-semibold">{t("dash.attention")}</h2>
              <p className="mb-2 text-xs text-[color:var(--ink-2)]">{t("dash.attentionWhy")}</p>
              {attention.length === 0 ? (
                <p className="text-sm">{t("dash.attentionNone")}</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {attention.map((lo) => (
                    <li key={lo.lo_id}><span className="mr-1 font-mono">{lo.code}</span>{lo.text} <span className="bignum text-xs">{Math.round(lo.secure_share * 100)}%</span></li>
                  ))}
                </ul>
              )}
            </section>
            <section className={card}>
              <h2 className="mb-2 font-semibold">{t("dash.checkIn")}</h2>
              {checkIn.length === 0 ? (
                <p className="text-sm">{t("dash.checkInNone")}</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {checkIn.map(({ p, i, notYet }) => (
                    <li key={p.respondent_id}>
                      <Link to="/t/c/$courseId/students/$personId" params={{ courseId, personId: p.respondent_id }} className="underline">{label(p.respondent_id, i)}</Link>{" "}
                      <span className="text-xs text-[color:var(--ink-2)]">{t("dash.notYetCount", { count: notYet })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </aside>
        </div>
      )}
    </TeacherChrome>
  );
}
